import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Heart, ThumbsUp, Check, Leaf } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * MindsetBriefCard — private coaching brief for the instructor, based on the
 * learner's most recent daily check-in. Supportive tone only, no labels.
 *
 * Renders in two slots:
 *   • NextLessonCard (on the ADI dashboard) — 30 min before the lesson
 *   • Work Mode pre-lesson screen — always, once in the flow
 *
 * Tracks metrics:
 *   brief_viewed           on first mount with checkin=true
 *   brief_helpful_clicked  when the instructor taps the helpful button
 */
export default function MindsetBriefCard({ studentId, context = "next_lesson" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [helpfulClicked, setHelpfulClicked] = useState(false);
  const viewedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    if (!studentId) { setLoading(false); return; }
    setLoading(true);
    axios
      .get(`${API}/api/work-mode/mindset-brief`, { params: { student_id: studentId }, withCredentials: true })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ checkin: false, message: "No check-in today", nudges: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId]);

  // Log view metric once, only when a real check-in is present.
  useEffect(() => {
    if (!data || !data.checkin || viewedRef.current) return;
    viewedRef.current = true;
    axios.post(`${API}/api/metrics/event`, {
      event: "brief_viewed",
      role: "instructor",
      testid: "mindset-brief",
      metadata: { context, student_id: studentId, tone: data.tone, nudge_count: (data.nudges || []).length },
    }, { withCredentials: true }).catch(() => {});
  }, [data, studentId, context]);

  const markHelpful = async () => {
    if (helpfulClicked) return;
    setHelpfulClicked(true);
    try {
      await axios.post(`${API}/api/metrics/event`, {
        event: "brief_helpful_clicked",
        role: "instructor",
        testid: "mindset-brief-helpful",
        metadata: { context, student_id: studentId, tone: data?.tone },
      }, { withCredentials: true });
    } catch { /* best-effort */ }
  };

  if (loading) {
    return (
      <p data-testid="mindset-brief-loading" className="text-xs text-slate-500 italic pt-5 mt-5 border-t border-slate-800/70">
        Loading coaching brief…
      </p>
    );
  }

  if (!data || !data.checkin) {
    // Activation Phase 1 #2 · red-button-active branch — flattened.
    if (data?.red_button_active) {
      return (
        <div data-testid="mindset-brief-red-button-only" className="pt-5 mt-5 border-t border-slate-800/70">
          <div className="flex items-center gap-2 mb-2">
            <Leaf className="w-3.5 h-3.5 text-rose-400" />
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Mindset brief · private</p>
          </div>
          <p data-testid="mindset-brief-red-button-headline" className="text-white font-bold text-sm leading-snug">
            {data.red_button_headline}
          </p>
          {data.red_button_opening && (
            <p data-testid="mindset-brief-red-button-opening" className="text-slate-300 text-sm mt-1.5 leading-relaxed">
              {data.red_button_opening}
            </p>
          )}
        </div>
      );
    }

    return (
      <div data-testid="mindset-brief-empty" className="pt-5 mt-5 border-t border-slate-800/70">
        <div className="flex items-center gap-2 mb-2">
          <Heart className="w-3.5 h-3.5 text-slate-500" />
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Mindset brief</p>
        </div>
        <p className="text-slate-300 text-sm leading-relaxed">
          No check-in today. <span className="text-slate-500 italic">We'll show you a brief once they've checked in.</span>
        </p>
      </div>
    );
  }

  const toneIconColor = ({
    support: "text-rose-400",
    protect: "text-emerald-400",
    mixed:   "text-amber-400",
  })[data.tone] || "text-slate-500";
  const toneDot = ({
    support: "bg-rose-400",
    protect: "bg-emerald-400",
    mixed:   "bg-amber-400",
  })[data.tone] || "bg-slate-500";

  return (
    <div data-testid="mindset-brief" className="pt-5 mt-5 border-t border-slate-800/70">
      <div className="flex items-center gap-2 mb-2">
        <Heart className={`w-3.5 h-3.5 ${toneIconColor}`} />
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Mindset brief · private</p>
      </div>
      <p className="text-white font-bold text-sm leading-snug">
        {data.message}
      </p>

      {/* Red Button quiet prep signal — inline text, no shaded panel. */}
      {data.red_button_active && (
        <div data-testid="mindset-brief-red-button" className="mt-2 flex items-start gap-2">
          <Leaf className="w-3.5 h-3.5 text-rose-400 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <p data-testid="mindset-brief-red-button-headline" className="text-rose-200 text-sm leading-snug">
              {data.red_button_headline}
            </p>
            {data.red_button_opening && (
              <p data-testid="mindset-brief-red-button-opening" className="text-rose-300/80 text-[13px] italic mt-1 leading-relaxed">
                {data.red_button_opening}
              </p>
            )}
          </div>
        </div>
      )}

      <ul data-testid="mindset-brief-nudges" className="mt-3 space-y-1.5">
        {(data.nudges || []).map((n, i) => (
          <li key={i} data-testid={`mindset-brief-nudge-${i}`} className="flex items-start gap-2 text-slate-300 text-sm leading-relaxed">
            <span className={`mt-2 w-1.5 h-1.5 rounded-full ${toneDot} flex-shrink-0`} />
            <span>{n}</span>
          </li>
        ))}
      </ul>

      {data.support_nudge && (
        <p data-testid="mindset-brief-support-level" className="mt-3 text-[13px] text-slate-400">
          <span className="text-slate-500">Pace: </span>{data.support_nudge}
        </p>
      )}

      <div className="mt-3 flex items-center justify-between">
        <p className="text-[11px] text-slate-500 italic">No judgement — just a steer</p>
        <button
          data-testid="mindset-brief-helpful-btn"
          onClick={markHelpful}
          disabled={helpfulClicked}
          className={`inline-flex items-center gap-1.5 text-[12px] font-semibold transition-colors ${
            helpfulClicked ? "text-emerald-400" : "text-slate-400 hover:text-white"
          } disabled:cursor-default`}
        >
          {helpfulClicked ? <Check className="w-3 h-3" /> : <ThumbsUp className="w-3 h-3" />}
          {helpfulClicked ? "Thanks — noted" : "Helpful"}
        </button>
      </div>
    </div>
  );
}
